import { useCallback, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Input as MuiInput, Typography, SelectChangeEvent } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Dayjs } from "dayjs";

import { CustomTable } from "../../components/DataTable";
import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import { ReactComponent as WarningIcon } from "../../assets/svg/WarningRed.svg";
import { RootState, useAppSelector } from "../../store";
import { colors } from "../../theme/colors";
import { CustomActionMenu } from "../../components/Menu";
import type { ActionItem } from "../../components/Menu";
import { useGetPathologyRegisterPatientSpecimenRecordsQuery } from "../../services/api";
import style from "./specimen.module.css";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import { SingleSelectInput } from "../../components/SelectInput";
import { requestFormStatus } from "../../utils/constants";
import DateInput from "../../components/UserInput/DateInput";
import { Button } from "../../components";

// Functional component representing the list of specimen information
const Specimens = () => {
  const navigate = useNavigate();
  const { userDetails, userRole } = useAppSelector((state: RootState) => state.auth);

  const [queryParam, setQueryParam] = useState<{
    search_key?: string;
    order_by_columns: string;
    order: string;
    is_discrepancy: boolean;
    status?: string;
    dropped_off_from?: string;
    dropped_off_till?: string;
  }>({
    search_key: "",
    order_by_columns: "created_at",
    order: "Dsc",
    is_discrepancy: false,
    status: undefined,
    dropped_off_from: undefined,
    dropped_off_till: undefined,
  });

  const [searchTerm, setSearchTerm] = useState(queryParam.search_key);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [fromDate, setFromDate] = useState<Dayjs | null>(null);
  const [toDate, setToDate] = useState<Dayjs | null>(null);

  type SpecimenAction = "view";
  const specimenActions: ActionItem<SpecimenAction>[] = [
    {
      label: "View",
      value: "view",
      icon: <EyeIcon height={24} width={24} />,
    },
  ];

  const fields = [
    {
      id: "patient_surname",
      label: "Patient Name",
      flex: 1.5,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${(item.patient_surname ?? "").toString().toUpperCase()}, ${item.patient_given_name ?? ""
            }`}
        </Typography>
      ),
    },
    {
      id: "laboratory",
      label: "Laboratory",
      flex: 1.5,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item["laboratory"] ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "surgeon_surname",
      label: "Surgeon",
      flex: 1.8,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${(item.surgeon_surname ?? "").toString().toUpperCase()}, ${item.surgeon_given_name ?? ""} ${item.surgeon_prefix ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "mrn_number",
      label: "MRN",
      flex: 1,
      type: "name",
      sortable: true,
      render: (item: PatientRecord) => (
        <Typography
          className={style.cell}
          sx={{
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: "2",
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {`${item.mrn_number ?? ""}`}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      type: "badge",
      flex: 1.8,
      sortable: true,
      render: (item: PatientRecord) =>
        item?.status ? (
          <Box
            className={style.statusContainer}
            sx={{
              backgroundColor:
                item?.status === "READY FOR PICKUP"
                  ? colors.lightGreen
                  : colors.modalBackground,
            }}
          >
            {item?.is_discrepancy && <WarningIcon width={16} height={16} />}
            <Typography className={style.cell} sx={{ fontSize: "12px" }}>
              {item?.status !== "None" ? item?.status : ""}
            </Typography>
          </Box>
        ) : (
          <></>
        ),
    },
    {
      id: "logo_s3_key_name",
      label: "Actions",
      type: "action",
      flex: 0.5,
      render: (item: PatientRecord) => (
        <CustomActionMenu<PatientRecord, SpecimenAction>
          item={item}
          isActive={item?.is_active ?? false}
          handleChange={handleMenu}
          actions={specimenActions}
        />
      ),
    },
  ];

  // for searching in the query
  const handleSearch = useCallback(
    (e: any) => {
      setSearchTerm(e.target.value ?? "");
    },
    []
  );

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQueryParam((prevParams) => ({
        ...prevParams,
        search_key: searchTerm === "" ? undefined : searchTerm,
      }));
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  // for handling the specimen state
  const handleMenu = (value: SpecimenAction, item: PatientRecord) => {
    if (value === "view") {
      navigate(`/specimens/details/${item?.id}`, {
        state: { isDiscrepancyView: selectedFilter === "Discrepancy" },
      });
    }
  };

  const healthOrgId = userDetails?.health_org_id;

  const handleStatusChange = useCallback(
    (event: SelectChangeEvent<unknown>) => {
      const value = (event.target.value as string) || "";
      setQueryParam((prev) => ({
        ...prev,
        status: value === "" ? undefined : value,
      }));
    },
    []
  );

  // Keep date filters in query params in sync with local state.
  useEffect(() => {
    setQueryParam((prev) => ({
      ...prev,
      dropped_off_from: fromDate ? fromDate.format("YYYY-MM-DD") : undefined,
      dropped_off_till: toDate ? toDate.format("YYYY-MM-DD") : undefined,
    }));
  }, [fromDate, toDate]);

  const {
    data: patientRecords,
    isFetching,
  } = useGetPathologyRegisterPatientSpecimenRecordsQuery(
    { healthOrgId: healthOrgId as number, query: queryParam },
    { skip: !healthOrgId }
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: "30px"
        }}
      >
        <MuiInput
          disableUnderline
          placeholder={
            (userRole === "OT_ADMIN")
              ? "Search by Patient/MRN/Laboratory/Surgeon"
              : userRole === "LABORATORY_ADMIN" ? "Search by Patient/MRN" : "Search by name...."
          }
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={searchTerm}
          onChange={handleSearch}
          sx={{
            width: "320px",
            gap: "8px",
            borderRadius: "50px",
            marginTop: "20px",
            backgroundColor: colors.white
          }}
        />
        <Button
          sx={{
            width: "140px",
            height: "56px",
            border: `1px solid ${colors.black}`,
            color: colors.black,
            backgroundColor:
              selectedFilter === "All" ? colors.lightGrey : colors.white,
            ":hover": {
              backgroundColor: colors.lightGrey,
              border: `1px solid ${colors.black}`,
            },
          }}
          variant="outlined"
          onClick={() => {
            setSelectedFilter("All");
            setQueryParam((prevParams) => ({
              ...prevParams,
              is_discrepancy: false,
            }));
          }}
          label={"All Specimens"}
        />
        <Button
          sx={{
            width: "190px",
            height: "56px",
            border: `1px solid ${colors.black}`,
            color: colors.black,
            backgroundColor:
              selectedFilter === "Discrepancy"
                ? colors.lightGrey
                : colors.white,
            ":hover": {
              backgroundColor: colors.lightGrey,
              border: `1px solid ${colors.black}`,
            },
          }}
          variant="outlined"
          onClick={() => {
            setSelectedFilter("Discrepancy");
            setQueryParam((prevParams) => ({
              ...prevParams,
              is_discrepancy: true,
            }));
          }}
          label={"Discrepant Specimens"}
        />
        <Box sx={{ width: 250 }}>
          <SingleSelectInput
            placeholder="Select status"
            label=""
            name="status"
            data={requestFormStatus ?? []}
            value={queryParam.status ?? ""}
            onChange={handleStatusChange}
            onClear={() => setQueryParam((prev) => ({ ...prev, status: undefined }))}
          />
        </Box>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateInput
            value={fromDate}
            onChange={setFromDate}
            label="From"
          />
          <DateInput
            value={toDate}
            onChange={setToDate}
            minDate={fromDate}
            label="To"
          />
        </LocalizationProvider>
      </Box>
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<PatientRecord>
          fields={fields}
          paginationRequired={true}
          data={patientRecords || []}
          loading={isFetching}
          onDoubleClick={(patientRecord: PatientRecord) => {
            navigate(`/specimens/details/${patientRecord?.id}`, {
              state: { isDiscrepancyView: selectedFilter === "Discrepancy" },
            });
          }}
          defaultOrderBy={"created_at"}
          searchTerm={searchTerm}
          onClearSearch={() => setSearchTerm("")}
        />
      </Box>
    </Box>
  );
};
export default Specimens;
